/**
 * @description Hooks for agent instance operations
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createAgentInstance, updateAgentInstance, BASE_URL } from '@/services/DataService';
import { logInfo, logger } from '@/lib/logger';

interface CreateAgentInstancePayload {
  agent_instance: {
    agent_id: string;
    config?: {
      [key: string]: unknown;
    };
  };
}

interface UpdateAgentInstancePayload {
  agent_instance: {
    config: {
      [key: string]: unknown;
    };
  };
}

export const useCreateAgentInstance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateAgentInstancePayload) => {
      if (!BASE_URL) {
        throw new Error('Base URL is not configured');
      }
      return createAgentInstance(payload);
    },
    onSuccess: (data) => {
      logInfo('Agent instance created successfully', { data });
      // Invalidate and refetch agent instances list
      queryClient.invalidateQueries({ queryKey: ['agent-instances'] });
    },
    onError: (error) => {
      logger(error as Error, { context: 'useCreateAgentInstance: Error creating agent instance' });
    },
  });
};

export const useUpdateAgentInstance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentInstanceId, payload }: { agentInstanceId: string; payload: UpdateAgentInstancePayload }) => {
      if (!BASE_URL) {
        throw new Error('Base URL is not configured');
      }
      return updateAgentInstance(agentInstanceId, payload);
    },
    onSuccess: (data) => {
      logInfo('Agent instance updated successfully', { data });
      // Invalidate and refetch agent instances list
      queryClient.invalidateQueries({ queryKey: ['agent-instances'] });
    },
    onError: (error) => {
      logger(error as Error, { context: 'useUpdateAgentInstance: Error updating agent instance' });
    },
  });
};